import { useEffect, useState } from "react";
import { apiClient } from "@/lib/api";
import { CATEGORIES } from "@/lib/constants";
import Reveal from "@/components/Reveal";
import { Loader2 } from "lucide-react";

export default function GalleryPage() {
    const [items, setItems] = useState([]);
    const [active, setActive] = useState("all");
    const [loading, setLoading] = useState(true);
    const [lightbox, setLightbox] = useState(null);

    useEffect(() => {
        setLoading(true);
        apiClient
            .get(`/gallery${active !== "all" ? `?category=${active}` : ""}`)
            .then((r) => setItems(r.data))
            .finally(() => setLoading(false));
    }, [active]);

    return (
        <div data-testid="gallery-page" className="container-padded section-spacing">
            <div className="max-w-3xl">
                <div className="overline mb-4">Selected Work</div>
                <h1 className="font-serif text-5xl md:text-6xl text-ink leading-[1.05]">A gallery of recent projects.</h1>
                <p className="text-stone-muted mt-5 text-lg">Furniture, kitchens, doors, wardrobes and full interior fit-outs — built in our workshop and installed on site.</p>
            </div>

            <div data-testid="gallery-filters" className="flex flex-wrap gap-2 mt-12 mb-12 border-b border-stone-border pb-2">
                {CATEGORIES.map((c) => (
                    <button
                        key={c.value}
                        data-testid={`gallery-filter-${c.value}`}
                        onClick={() => setActive(c.value)}
                        className={`px-4 py-2 text-sm tracking-wide transition-colors ${
                            active === c.value ? "text-walnut border-b-2 border-walnut -mb-[10px]" : "text-stone-muted hover:text-walnut"
                        }`}
                    >
                        {c.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="py-20 flex justify-center text-walnut"><Loader2 className="animate-spin" /></div>
            ) : items.length === 0 ? (
                <div className="py-20 text-center text-stone-muted">No projects in this category yet.</div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                    {items.map((item, i) => (
                        <Reveal key={item.id} delay={(i % 6) * 60}>
                            <div
                                data-testid={`gallery-item-${item.id}`}
                                onClick={() => setLightbox(item)}
                                className="group cursor-pointer bg-sand"
                            >
                                <div className="aspect-[4/5] overflow-hidden">
                                    <img src={item.image_url} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                </div>
                                <div className="p-5">
                                    <div className="font-serif text-xl text-ink">{item.title}</div>
                                    <div className="text-xs uppercase tracking-widest text-walnut-light mt-1">
                                        {CATEGORIES.find((c) => c.value === item.category)?.label || item.category}
                                    </div>
                                    {item.description && (
                                        <p className="text-sm text-stone-muted mt-3 leading-relaxed line-clamp-2">{item.description}</p>
                                    )}
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            )}

            {lightbox && (
                <div
                    data-testid="gallery-lightbox"
                    className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4 md:p-12"
                    onClick={() => setLightbox(null)}
                >
                    <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
                        <img src={lightbox.image_url} alt={lightbox.title} className="w-full max-h-[75vh] object-contain" />
                        <div className="text-cream mt-5">
                            <div className="font-serif text-3xl">{lightbox.title}</div>
                            {lightbox.description && <p className="text-stone-300 mt-2">{lightbox.description}</p>}
                        </div>
                        <button
                            data-testid="gallery-lightbox-close"
                            onClick={() => setLightbox(null)}
                            className="mt-6 border border-cream/60 text-cream px-6 py-2 hover:bg-cream hover:text-ink transition-colors text-sm tracking-wide"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
